import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, Inject, QueryList, TemplateRef, ViewChildren } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslocoDirective } from '@jsverse/transloco';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { RippleModule } from 'primeng/ripple';
import { SelectModule } from 'primeng/select';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { Subscription } from 'rxjs';
import { SettingsCard, SettingsSection } from '../../interfaces/generic.interface';
import { AuthStateService } from '../../services/authstate.service';
import { NotificationsService } from '../../services/notifications.service';
import { InputNumberModule } from 'primeng/inputnumber';

@Component({
  selector: 'app-admin-settings-view',
  imports: [
    RippleModule,
    FormsModule,
    FloatLabelModule,
    InputTextModule,
    CheckboxModule,
    SelectModule,
    TranslocoDirective,
    ButtonModule,
    CommonModule,
    PasswordModule,
    ToggleButtonModule,
    CheckboxModule,
    InputNumberModule
  ],
  templateUrl: './admin-settings-view.component.html',
  styleUrl: './admin-settings-view.component.scss'
})
export class AdminSettingsViewComponent implements AfterViewInit {

  @ViewChildren(TemplateRef) templates!: QueryList<TemplateRef<any>>;

  activeSection: string = "viewing";

  mediumRegex = '^(((?=.*[a-z])(?=.*[A-Z]))|((?=.*[a-z])(?=.*[0-9]))|((?=.*[A-Z])(?=.*[0-9])))(?=.{6,})';
  strongRegex = '^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[@$!%*?&#])(?=.{8,})';

  updateSub?: Subscription;


  dataSections: SettingsSection[] = [
    {
      id: "viewing",
      title: "viewing",
      icon: "visibility",
      cards: [
        {
          title: "map",
          originalData: {
            defaultLat: localStorage.getItem("defaultLat") ?? 40.955854,
            defaultLng: localStorage.getItem("defaultLng") ?? 17.288318,
          },
          data: {
            defaultLat: localStorage.getItem("defaultLat") ?? 40.955854,
            defaultLng: localStorage.getItem("defaultLng") ?? 17.288318,
          },
          isEditing: false,
          templateName: "viewMapSettings",
          updateFunction: (newData, successCb, errorCb) => {
            localStorage.setItem("defaultLat", newData.defaultLat)
            localStorage.setItem("defaultLng", newData.defaultLng)
            successCb()
          }
        },
      ]
    },
  ]

  get currentSectionCards() {
    return this.dataSections.find(section => section.id === this.activeSection)!.cards;
  }

  constructor(
    private authState: AuthStateService,
    private http: HttpClient,
    private notifications: NotificationsService,
    private router: Router,
    @Inject('ENVIRONMENT') private ENV: any
  ) {
  }

  ngAfterViewInit(): void {
    const templateElements = this.templates.toArray();

    setTimeout(() => {

      this.dataSections.forEach(section => {
        section.cards.forEach(card => {
          // Find template by name from the DOM
          const template = templateElements.find(t =>
            (t as any)['_declarationTContainer']?.localNames?.[0] === card.templateName
          );
          if (template) {
            card.template = template;
          }
          this.refreshCardData(card)
        });
      });
    }, 1);
  }

  cardEnableEdit(card: SettingsCard) {
    card.isEditing = true;
    card.data = structuredClone(card.originalData);
  }

  cardCancelEdits(card: SettingsCard) {
    card.isEditing = false;
    card.data = structuredClone(card.originalData);
    if (this.updateSub) {
      this.updateSub.unsubscribe();
      this.updateSub = undefined;
      card.isLoading = false;
    }
  }

  cardSaveEdits(card: SettingsCard) {

    if (card.updateEndpoint) {
      if (this.updateSub) {
        this.updateSub.unsubscribe();
        this.updateSub = undefined;
        card.isLoading = false;
      }
      card.isLoading = true;
      this.updateSub = this.http.post(this.ENV.apiUrl + card.updateEndpoint, card.updateTransform ? card.updateTransform(card.data) : card.data).subscribe({
        next: (res: any) => {
          card.isLoading = false;
          if (!(card.afterUpdateOverride ?? false)) {

            card.isEditing = false;
            card.originalData = structuredClone(card.data);
            if (res.code == 200) {
              this.notifications.showNotification({
                severity: "success",
                title: "operationComplete",
                detail: "dataUpdatedCorrectly",
                addToHistory: false
              })
            } else if (res.code == 201) {
              this.notifications.showNotification({
                severity: "info",
                title: "noModifications",
                addToHistory: false
              })
            }
          }
          if (card.afterUpdate) {
            card.afterUpdate(card.data, res);
          }

          this.refreshCardData(card);
        },
        error: (err: any) => {
          card.isLoading = false;
        }
      });
    }
    if (card.updateFunction) {
      card.updateFunction(card.updateTransform ? card.updateTransform(card.data) : card.data, () => {
        card.isEditing = false;
        card.originalData = structuredClone(card.data);
        this.notifications.showNotification({
          severity: "success",
          title: "operationComplete",
          detail: "dataUpdatedCorrectly",
          addToHistory: false
        })
        if (card.afterUpdate) {
          card.afterUpdate(card.data);
        }
      }, () => {
        card.isLoading = false;
      })
    }
  }

  loadForm(card: SettingsCard, form: NgForm) {
    card.form = form;
    return '';
  }

  refreshCardData(card: SettingsCard) {
    if (card.getEndpoint) {
      card.isLoading = true;
      this.http.post(this.ENV.apiUrl + card.getEndpoint, card.getParameters).subscribe({
        next: (res: any) => {
          card.isLoading = false;
          card.data = structuredClone(res.data);
          card.originalData = structuredClone(res.data);
        },
        error: (err) => {
          card.isLoading = false;
        }
      });
    }
  }
}
